// The Systems workspace's seven top-level façade panels (Logic / HUD / Audio
// / FX / Data / Cutscenes / Intro Sequences). Each is registered with no
// `zone`/`group` in screens-editor.tsx's buildPanelRegistry, so they collapse
// into one flat dockview tab strip - the same mechanism that already makes
// Setup's "Rules/Camera/World/Meta" row look like one clean panel (see
// panel-system/Workspace.tsx's UNGROUPED bucketing). Before this file
// existed, each subsystem's list/editor/reference pieces
// (docs/editor-consolidation/panel-content-spec.md) were registered as
// separate dockview panels with an explicit `zone`/`group`, which produced a
// dockview group per subsystem stacked vertically within each zone - the
// "sheer volume of closeable tabs" the editor-consolidation follow-up review
// found. These façades compose the exact same panel-body components
// (editor-modes2.tsx / editor-data-fx.tsx / CutsceneTimeline.tsx /
// IntroSequenceTimeline.tsx) into one fixed, non-draggable flex layout
// instead - no changes to any of those components' own internals, since they
// already read/write cross-panel selection via the existing
// transientSelection stores and already self-wrap in M2Panel's fixed-width
// side chrome.
import { useState } from 'react';

import {
  LogicRulesListPanel, LogicRuleEditorPanel, LogicReferencePanel,
  HudWidgetListPanel, HudStagePanel, HudPropertiesPanel,
  AudioLibraryPanel, AudioMixerPanel, AudioPreviewPanel, AudioZonesPanel,
} from './editor-modes2';
import {
  GameDataListPanel, GameDataEditorPanel,
  FxMaterialListPanel, FxMaterialPreviewPanel, FxMaterialPropertiesPanel, FxParticlesLegacyRoute,
} from './editor-data-fx';
import { CutsceneRoute } from './CutsceneTimeline';
import { IntroRoute } from './IntroSequenceTimeline';
import { GsSeg } from './editor-panels';

export function SystemsLogicPanel() {
  return (
    <div style={{ display: 'flex', flex: 1, minWidth: 0, minHeight: 0 }}>
      <LogicRulesListPanel />
      <LogicRuleEditorPanel />
      <LogicReferencePanel />
    </div>
  );
}

export function SystemsHudPanel() {
  return (
    <div style={{ display: 'flex', flex: 1, minWidth: 0, minHeight: 0 }}>
      <HudWidgetListPanel />
      <HudStagePanel />
      <HudPropertiesPanel />
    </div>
  );
}

/** Library+Mixer share the left column (stacked, not tabbed) and Preview+
 * Zones share the center column - the pre-panel-system layout
 * (panel-content-spec.md §7), restored here now that nothing needs them
 * split into independent dockview panels. */
export function SystemsAudioPanel() {
  return (
    <div style={{ display: 'flex', flex: 1, minWidth: 0, minHeight: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <AudioLibraryPanel />
        <AudioMixerPanel />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <AudioPreviewPanel />
        <AudioZonesPanel />
      </div>
    </div>
  );
}

/** Restores FxRoute's original Materials/Particles segmented control
 * (panel-content-spec.md §9) - Particles never got split into sibling
 * panels (it still owns a live particle-preview Canvas pending the shared-
 * viewport work), so it stays one full-width view exactly as
 * FxParticlesLegacyRoute already renders it. Only the active view's canvas
 * mounts, one fewer live WebGL context than today's "everything in Systems
 * stays mounted" dockview behaviour. */
export function SystemsFxPanel() {
  const [view, setView] = useState<'Materials' | 'Particles'>('Materials');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--ss-border-soft)', flexShrink: 0 }}>
        <GsSeg options={['Materials', 'Particles']} value={view} onChange={(v) => setView(v as 'Materials' | 'Particles')} listLabel="FX view" />
      </div>
      {view === 'Materials' ? (
        <div style={{ display: 'flex', flex: 1, minWidth: 0, minHeight: 0 }}>
          <FxMaterialListPanel />
          <FxMaterialPreviewPanel />
          <FxMaterialPropertiesPanel />
        </div>
      ) : (
        <FxParticlesLegacyRoute />
      )}
    </div>
  );
}

export function SystemsDataPanel() {
  return (
    <div style={{ display: 'flex', flex: 1, minWidth: 0, minHeight: 0 }}>
      <GameDataListPanel />
      <GameDataEditorPanel />
    </div>
  );
}

/** Thin wrappers so Cutscenes and Intro Sequences each get their own
 * top-level tab, rather than today's arbitrary pairing (both happened to
 * declare no `group`, so they shared one dockview group by accident, not by
 * design). */
export function SystemsCutscenePanel() {
  return <CutsceneRoute />;
}

export function SystemsIntroPanel() {
  return <IntroRoute />;
}
